import {FormField} from 'course/application/firebase/models/formData';
import { FormikErrors, FormikTouched } from 'formik';

export interface LocationData {
  locality: string;
  coordinates?: string;
  /**
   * ISO 3166-1 alpha-2 country code (e.g. `US`). Resolved through Places, or
   * picked by the applicant for a manual entry. Mirrored to the applicant's
   * user profile on submit.
   */
  country?: string;
  /**
   * True when the applicant typed `locality` by hand because the place lookup
   * was unreachable. Such an entry was never resolved through Places, so it
   * carries no `coordinates`.
   */
  isManualEntry?: boolean;
}

/** Shortest manually typed locality {@link normalizeManualLocality} accepts. */
export const MANUAL_LOCALITY_MIN_LENGTH = 2;

/** Longest manually typed locality {@link normalizeManualLocality} accepts. */
export const MANUAL_LOCALITY_MAX_LENGTH = 100;

/**
 * Cleans up a locality the applicant typed by hand, for use when the place
 * lookup is unreachable and there is no suggestion to pick.
 *
 * Double quotes and control characters are replaced with spaces, runs of
 * whitespace collapse to one space, and the ends are trimmed. The result is
 * accepted when it contains at least one letter and its length falls in the
 * closed interval
 * `[MANUAL_LOCALITY_MIN_LENGTH, MANUAL_LOCALITY_MAX_LENGTH]`, both bounds
 * inclusive.
 *
 * @param text - What the applicant typed.
 * @return The cleaned locality, or `null` when it is not acceptable.
 * @throws Never.
 */
export function normalizeManualLocality(text: string): string | null {
  const normalized = text
    // The admin CSV export writes fields unquoted and does not escape quotes.
    .replace(/["\p{Cc}]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const hasAcceptableLength =
    normalized.length >= MANUAL_LOCALITY_MIN_LENGTH &&
    normalized.length <= MANUAL_LOCALITY_MAX_LENGTH;
  if (!hasAcceptableLength || !/\p{L}/u.test(normalized)) {
    return null;
  }
  return normalized;
}

export interface GeolocatorProps {
  field: FormField;
  name: string;
  values: Record<string, LocationData>;
  touched: FormikTouched<LocationData>;
  errors: FormikErrors<LocationData>;
  setFieldValue: (field: string, value: LocationData | null) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
}

export const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000,
};
